import { formatProductSize } from '~/utils/format-product-size';
/**
 * 🎯 UNIVERSAL FILTER MENU - CONSOLIDATED FILTER SYSTEM
 *
 * This component replaces 4 separate filter menu components (EnhancedCategoryFilterMenu,
 * EnhancedProductFilterMenu, EnhancedDealsFilterMenu, EnhancedShopFilterMenu) with a
 * single, configurable component that adapts behavior based on page type.
 *
 * 🔧 KEY TECHNICAL FEATURES:
 * - Configurable navigation vs multi-select behavior per page type
 * - Dynamic brand switcher with pagination (10 brands + "View More")
 * - Subtype and size filters limited to 10 items with "View More" button
 * - Deals toggle (onsale filter) with availability checking
 * - Auto-clear invalid filter combinations on every facet change
 * - Filters automatically clear when navigating between pages (matches Embarc pattern)
 * - Loading states with spinner and blur overlay during filter updates
 * - Non-sticky desktop sidebar (matches Embarc pattern)
 * - WCAG AAA compliant focus-visible rings (keyboard navigation only)
 *
 * 📱 MOBILE UX OPTIMIZATIONS:
 * - Clean edge-to-edge mobile drawer layout (full width)
 * - Fixed "Show Results" button at bottom with product count
 * - Dynamic viewport height (dvh) for proper mobile sizing
 * - Hidden scrollbar on mobile filter menu
 * - Dark body background when mobile menu opens (prevents white flash)
 * - Proper focus trapping with useFocusTrap hook
 *
 * 🎛️ PAGE-SPECIFIC CONFIGURATIONS:
 * - **Category Pages**: Category filter hidden, multi-select other filters
 * - **Brand Pages**: Brand filter hidden, multi-select categories + brand switcher
 * - **Shop Pages**: Category open by default, multi-select all filters
 * - **Deals Pages**: Deals toggle hidden (always on), multi-select other filters
 * - **Homepage**: Category open by default, multi-select all filters
 *
 * 🔗 RELATED FILES:
 * - ~/hooks/use-focus-trap.ts - Mobile focus trapping and scroll prevention
 * - ~/routes/_layout.brand.$slug/route.tsx - Example implementation
 * - ~/api/*-filtering.server.ts - Server-side filtering APIs
 *
 * 🚀 USAGE EXAMPLE:
 * ```tsx
 * const filterConfig: UniversalFilterMenuConfig = {
 *   pageType: 'brand',
 *   visibleFilters: { category_type: true, subtype: true, size: true },
 *   navigation: {}, // No navigation = multi-select behavior
 *   currentContext: { brandSlug, brandLabel },
 *   defaultOpenSections: ['brand', 'category_type']
 * };
 *
 * <UniversalFilterMenu
 *   config={filterConfig}
 *   contextualFacets={contextualFacets}
 *   baseFacets={baseFacets}
 *   total={total}
 *   dealsAvailable={dealsAvailable}
 *   isLoading={isFiltersLoading}
 *   onLoadingChange={setIsFiltersLoading}
 * />
 * ```
 */

import { useState, useEffect, useMemo, useRef, useTransition } from 'react';
import { toBrandSlug } from '~/utils/brand-slug';
import { useSearchParams, useNavigate, useNavigation, useLocation } from '@remix-run/react';
import type { FilterFacets } from '~/api/brand-filtering.server';
import { useFocusTrap } from '~/hooks/use-focus-trap';
import { klaviyo } from '~/utils/analytics';

// No counter needed - using simple static IDs like the old filter sidebar

// Configuration interface for different page types
export interface UniversalFilterMenuConfig {
  pageType: 'category' | 'brand' | 'shop' | 'deals' | 'homepage';

  // Which filters to show
  visibleFilters: {
    category_type?: boolean;
    brand?: boolean;
    subtype?: boolean;
    classification?: boolean;
    size?: boolean;
    discount_applied?: boolean;
  };

  // Navigation behavior (SEO-driven pages)
  navigation: {
    categoryNavigation?: boolean; // Navigate to /category/{slug}
    brandNavigation?: boolean; // Navigate to /brand/{slug}
  };

  // Current context (to exclude from switchers)
  currentContext?: {
    categorySlug?: string;
    categoryLabel?: string;
    brandSlug?: string;
    brandLabel?: string;
  };

  // Labels for different contexts
  labels?: {
    category_type?: string; // "Category" vs "Deal Categories"
    [key: string]: string | undefined;
  };

  // Default expanded sections
  defaultOpenSections?: string[];
}

export interface UniversalFilterMenuProps {
  config: UniversalFilterMenuConfig;
  baseFacets: FilterFacets;
  contextualFacets: FilterFacets;
  total: number;
  /** When true, behaves as a modal dialog (mobile drawer) with focus trapped inside */
  trapFocus?: boolean;
  /** Optional close handler, used on Escape when trapFocus is true */
  onRequestClose?: () => void;
  /** Optional instance identifier to prevent duplicate IDs when multiple instances exist */
  instanceId?: string;
  /** Loading state from parent component */
  isLoading?: boolean;
  /** Callback to notify parent of loading state changes */
  onLoadingChange?: (loading: boolean) => void;
  /** Number of products with deals available for current filters (excluding deals filter) */
  dealsAvailable?: number;
}

interface FilterOption {
  value: string;
  count: number;
  isSelected: boolean;
  isAvailable: boolean;
}

interface FilterSection {
  key: keyof FilterFacets;
  label: string;
  urlParam: string;
  isVisible: boolean;
  hasNavigation: boolean; // true if this filter triggers navigation
}

// Category options for navigation (fallback - should use dynamic data when available)
const FALLBACK_CATEGORY_OPTIONS = [
  { value: 'FLOWER', label: 'Flower', slug: 'flower' },
  { value: 'VAPES', label: 'Vapes', slug: 'vapes' },
  { value: 'EDIBLES', label: 'Edibles', slug: 'edibles' },
  { value: 'CONCENTRATES', label: 'Concentrates', slug: 'concentrates' },
  { value: 'PREROLLS', label: 'Prerolls', slug: 'prerolls' },
  { value: 'TOPICALS', label: 'Topicals', slug: 'topicals' },
  { value: 'TINCTURES', label: 'Tinctures', slug: 'tinctures' },
  { value: 'BEVERAGES', label: 'Drinks', slug: 'beverages' }, // Display as "Drinks" but slug is "beverages"
];

// Utility function to convert uppercase text to proper case
// Handles special abbreviations: CBD, I/S, S/I
const toProperCase = (text: string): string => {
  // Handle special abbreviations that should remain uppercase
  const upperCaseAbbreviations: Record<string, string> = {
    cbd: 'CBD',
    'i/s': 'I/S',
    's/i': 'S/I',
  };

  const lowerText = text.toLowerCase();

  // Check if the entire text matches a special abbreviation
  if (upperCaseAbbreviations[lowerText]) {
    return upperCaseAbbreviations[lowerText];
  }

  // Check if any word in the text matches a special abbreviation
  return text
    .toLowerCase()
    .split(' ')
    .map((word) => {
      // Check if this word is a special abbreviation
      if (upperCaseAbbreviations[word]) {
        return upperCaseAbbreviations[word];
      }
      // Otherwise, capitalize normally
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

export default function UniversalFilterMenu({
  config,
  baseFacets,
  contextualFacets,
  total,
  trapFocus = false,
  onRequestClose,
  instanceId,
  isLoading = false,
  onLoadingChange,
  dealsAvailable = 0,
}: UniversalFilterMenuProps) {
  // Generate stable unique ID suffix to avoid duplicate IDs (SSR-safe)
  // Use simple static IDs like the old filter sidebar - no complex generation needed
  const uniqueId = useMemo(() => {
    if (instanceId) {
      return `${config.pageType}-${instanceId}`;
    }
    const instanceType = trapFocus ? 'mobile' : 'desktop';
    return `${config.pageType}-${instanceType}`;
  }, [config.pageType, trapFocus, instanceId]);

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition(); // For smooth transitions
  const navigation = useNavigation(); // For detecting navigation state
  const location = useLocation(); // For checking current route

  // Track if user is manually changing filters (prevent auto-clear during manual changes)
  const isManualChange = useRef(false);

  // Track last contextual facets to detect changes (Remix pattern)
  const lastContextualFacetsRef = useRef<string>('');

  // Track last time we cleared all filters to prevent loops (matches Embarc pattern)
  const lastClearedAllFiltersRef = useRef<number>(0);

  // Track pathname to detect route changes (for skipping loading on navigation)
  const isNavigatingToNewPage =
    navigation.location && navigation.location.pathname !== location.pathname;

  // Combined loading state - either internal transition or external loading
  // ⚡ PERFORMANCE: Don't show loading spinner when navigating to a new page
  // Only show loading when actually filtering on the same page
  // Matches Embarc's simple approach - parent component manages loading state
  const isFilterLoading = !isNavigatingToNewPage && (isPending || isLoading);

  // Calculate dealsAvailable from contextualFacets if not provided
  // This represents products with deals available for current filters (excluding deals filter)
  // If deals filter is already active, use total (all current products have deals)
  // Use baseFacets if no filters are active (shows all available deals)
  const isDealsFilterActive = searchParams.get('deals') === 'true';
  const hasActiveFilters = Array.from(searchParams.entries()).some(
    ([key, value]) =>
      key !== 'page' && key !== 'sort' && key !== 'search' && key !== 'deals' && value,
  );

  // Use dealsAvailable prop directly (calculated from onsale:=true search, like Embarc)
  // If deals filter is active, all current products have deals, so use total
  const calculatedDealsAvailable = isDealsFilterActive ? total : dealsAvailable;

  // Debug: Log deals availability for troubleshooting (dev mode only)
  // Use useEffect to prevent logging on every render - only log when values change
  const prevDealsRef = useRef<{
    dealsAvailable?: number;
    calculatedDealsAvailable: number;
    isDealsFilterActive: boolean;
    total: number;
  } | null>(null);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const current = {
        dealsAvailable,
        calculatedDealsAvailable,
        isDealsFilterActive,
        total,
      };

      // Only log if values actually changed
      if (
        !prevDealsRef.current ||
        prevDealsRef.current.dealsAvailable !== current.dealsAvailable ||
        prevDealsRef.current.calculatedDealsAvailable !== current.calculatedDealsAvailable ||
        prevDealsRef.current.isDealsFilterActive !== current.isDealsFilterActive ||
        prevDealsRef.current.total !== current.total
      ) {
        console.log('🔍 [DEALS TOGGLE]', {
          dealsAvailable,
          calculatedDealsAvailable,
          isDealsFilterActive,
          total,
          'config.visibleFilters.discount_applied': config.visibleFilters.discount_applied,
        });
        prevDealsRef.current = current;
      }
    }
  }, [
    dealsAvailable,
    calculatedDealsAvailable,
    isDealsFilterActive,
    total,
    config.visibleFilters.discount_applied,
  ]);

  // 🔥 SMART AUTO-CLEAR INVALID FILTERS (matches Embarc pattern)
  // Strategy: Only clear filters with 0 count, skip during manual changes and navigation
  useEffect(() => {
    // Skip if user is manually changing filters
    if (isManualChange.current) {
      isManualChange.current = false;
      return;
    }

    // Skip if navigating to a new page (don't auto-clear during navigation)
    if (isNavigatingToNewPage) {
      return;
    }

    // Skip if navigation is pending (don't auto-clear while loading)
    if (navigation.state === 'loading') {
      return;
    }

    // Create a stable string representation of contextualFacets to detect real changes
    const currentFacetsString = JSON.stringify(contextualFacets);

    // Skip if contextualFacets haven't actually changed
    if (currentFacetsString === lastContextualFacetsRef.current) {
      return;
    }

    lastContextualFacetsRef.current = currentFacetsString;

    // Check if any selected filters now have 0 count in contextualFacets
    const currentFilters = Array.from(searchParams.entries()).filter(([key]) =>
      ['brand', 'category', 'subtype', 'classification', 'size'].includes(key),
    );

    if (currentFilters.length === 0) {
      return; // No filters to check
    }

    const newParams = new URLSearchParams(searchParams);
    let clearedAny = false;
    const clearedFilters: string[] = [];

    // Check each filter type for invalid values
    // Note: 'category' URL param maps to 'category_type' facet
    ['brand', 'category', 'subtype', 'classification', 'size'].forEach((filterType) => {
      const currentValue = searchParams.get(filterType);
      if (!currentValue) return;

      const values = currentValue
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v.length > 0);

      // Map URL param to facet key ('category' → 'category_type')
      const facetKey = (
        filterType === 'category' ? 'category_type' : filterType
      ) as keyof FilterFacets;
      // Enhanced null/undefined safety (matches Embarc pattern)
      const contextualOptions = contextualFacets?.[facetKey] || [];

      // Filter out values that don't exist in contextual facets OR have 0 count
      const validValues = values.filter((value) =>
        contextualOptions.some((opt: any) => opt.value === value && opt.count > 0),
      );

      // If some values are now invalid (not in contextual or have 0 count), remove them
      if (validValues.length !== values.length) {
        const removedValues = values.filter((v) => !validValues.includes(v));

        if (validValues.length === 0) {
          newParams.delete(filterType);
          clearedFilters.push(`${filterType}: ${removedValues.join(', ')}`);
        } else {
          newParams.set(filterType, validValues.join(','));
          clearedFilters.push(`${filterType}: ${removedValues.join(', ')}`);
        }
        clearedAny = true;
      }
    });

    // 🚨 FALLBACK: If we didn't clear any filters but total is still 0,
    // it means all filters individually have count > 0 but together they result in 0 products.
    // Instead of clearing ALL filters, try clearing filters one type at a time, starting with least important
    // This matches Embarc's industry-grade error handling pattern
    if (!clearedAny && total === 0) {
      const now = Date.now();
      const timeSinceLastClear = now - lastClearedAllFiltersRef.current;

      // Prevent clearing filters if we just did it recently (within 1 second)
      // This prevents loops when filters are being cleared and re-applied
      if (timeSinceLastClear < 1000) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🚨 [Total=0 Fallback] Skipping - cleared filters recently, preventing loop');
        }
        return;
      }

      const filterPriority = ['size', 'classification', 'subtype', 'category', 'brand'];

      // Find the first filter type that has values and clear it
      for (const filterType of filterPriority) {
        const currentValue = searchParams.get(filterType);
        if (currentValue) {
          if (process.env.NODE_ENV === 'development') {
            console.log(
              `🚨 [Total=0 Fallback] Clearing ${filterType} filter (least important) to break incompatible combination`,
            );
          }

          newParams.delete(filterType);
          lastClearedAllFiltersRef.current = now;
          clearedAny = true;
          clearedFilters.push(`${filterType}: ${currentValue} (incompatible combination)`);
          break; // Only clear one filter type at a time
        }
      }
    }

    // Reset loop prevention when filters are manually changed or total > 0
    if (isManualChange.current || total > 0 || isNavigatingToNewPage) {
      lastClearedAllFiltersRef.current = 0;
    }

    // If we cleared any filters, update the URL silently (use replace to avoid history pollution)
    if (clearedAny) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🧹 Auto-clearing filters with 0 count:', clearedFilters);
      }
      newParams.delete('page'); // Reset to page 1
      startTransition(() => {
        // Use preventScrollReset to avoid double loading state
        setSearchParams(newParams, { replace: true, preventScrollReset: true });
      });

      klaviyo.track('Filters Auto-Cleared', {
        page_type: config.pageType,
        reason: clearedFilters.some((f) => f.includes('incompatible'))
          ? 'incompatible_combination'
          : 'zero_count_filters',
        cleared_filters: clearedFilters,
        timestamp: new Date().toISOString(),
      });
    }
  }, [
    contextualFacets,
    searchParams,
    setSearchParams,
    config.pageType,
    isNavigatingToNewPage,
    navigation.state,
    total, // Add total to dependencies to trigger fallback when total === 0
  ]);

  // Clear all filters when store changes to prevent "no products found" scenarios
  useEffect(() => {
    const handleStoreChange = () => {
      // Force a full page reload to the clean URL to ensure everything resets,
      // but carry the marketing parameters across.
      //
      // This previously reloaded to `window.location.pathname` alone, which
      // cleared the filters as intended and discarded the campaign parameters
      // as a side effect. That matters on a first visit from a Google Business
      // Profile link: the age gate dispatches `store:change` on confirm, this
      // reload fires ~100ms later, and GTM is deferred 8s (useGTM.ts:44) — so
      // for anyone who clicks through the gate in under 8 seconds, GTM loads
      // after the wipe and reports the pageview with no campaign attribution
      // at all. The visit lands as direct rather than against its
      // <city>-bopis campaign.
      //
      // Allowlist rather than preserving the whole query string: filters live
      // in the query too, and clearing them is the entire point of this reload.
      setTimeout(() => {
        const currentUrl = new URL(window.location.href);
        const marketingParams = new URLSearchParams();

        currentUrl.searchParams.forEach((value, key) => {
          if (key.startsWith('utm_') || ['gclid', 'fbclid', 'msclkid'].includes(key)) {
            marketingParams.append(key, value);
          }
        });

        const queryString = marketingParams.toString();
        window.location.href = currentUrl.pathname + (queryString ? `?${queryString}` : '');
      }, 100); // Slightly longer delay to ensure store change completes

      // Track the filter clearing for analytics
      klaviyo.track('Filters Cleared', {
        page_type: config.pageType,
        reason: 'store_change',
        timestamp: new Date().toISOString(),
      });
    };

    // Listen for store change events
    window.addEventListener('store:change', handleStoreChange);

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener('store:change', handleStoreChange);
    };
  }, [config.pageType]);

  // Get dynamic category options from API data (prefer baseFacets for all available categories)
  const getCategoryOptions = () => {
    const categoryFacets = baseFacets?.category_type || [];

    // Special case: Category pages don't get category_type in baseFacets (it's intentionally empty)
    // So we always use the fallback options to show all available categories
    if (config.pageType === 'category') {
      return FALLBACK_CATEGORY_OPTIONS.map((option) => ({
        ...option,
        count: 0, // We don't have counts for category pages, but that's fine
      }));
    }

    // For all other pages (deals, shop, homepage, brand), use dynamic data if available
    if (categoryFacets.length > 0) {
      // Use dynamic data from API with special mappings
      return categoryFacets
        .filter((facet) => {
          // Filter out invalid category types (subtypes that are miscategorized)
          const invalidCategories = ['PILL', 'CAPSULE', 'TABLET', 'GUMMY', 'CHOCOLATE', 'COOKIE'];
          if (invalidCategories.includes(facet.value.toUpperCase())) {
            return false;
          }

          // For deals page: only show categories that actually have deals (count > 0)
          if (config.pageType === 'deals') {
            return facet.count > 0;
          }
          // For other pages: show all categories
          return true;
        })
        .map((facet) => {
          // Handle special mappings
          let label = toProperCase(facet.value);
          let slug = facet.value.toLowerCase();
          let mappedValue = facet.value;

          if (facet.value === 'CARTRIDGE') {
            label = 'Vapes';
            slug = 'vapes';
          } else if (facet.value === 'BEVERAGE') {
            label = 'Drinks';
            slug = 'beverages'; // Use plural for URL consistency
          } else if (facet.value === 'PREROLL') {
            label = 'Prerolls'; // Plural form
            slug = 'prerolls';
          } else if (facet.value === 'EXTRACT') {
            label = 'Concentrates'; // More user-friendly term
            slug = 'concentrates';
          } else if (facet.value === 'EDIBLE') {
            label = 'Edibles'; // Plural form
            slug = 'edibles';
            // Keep original value for filtering - don't change mappedValue
          }

          return {
            value: mappedValue, // Use mapped value for filtering
            label,
            slug,
            count: config.pageType === 'deals' ? 0 : facet.count, // No counts on deals page
          };
        });
      // No need for complex merging since PILL is not a category_type
    }

    // Fallback to hardcoded options if no API data
    return FALLBACK_CATEGORY_OPTIONS.map((option) => ({
      ...option,
      count: 0,
    }));
  };

  const categoryOptions = getCategoryOptions();

  // Build filter sections based on config
  // Note: Deals toggle is rendered separately at the top
  // Match Embarc's behavior: Hide category filter on category pages, hide brand filter on brand pages
  // Order: Category before Brand (for shop and homepage)
  const FILTER_SECTIONS: FilterSection[] = [
    {
      key: 'category_type' as keyof FilterFacets,
      label: config.labels?.category_type || 'Category',
      urlParam: 'category',
      isVisible: config.visibleFilters.category_type !== false && config.pageType !== 'category', // Hide on category pages
      hasNavigation: false, // Categories are now multi-select filters (no navigation)
    },
    {
      key: 'brand' as keyof FilterFacets,
      label: 'Brand',
      urlParam: 'brand',
      isVisible: config.visibleFilters.brand !== false && config.pageType !== 'brand', // Hide on brand pages
      hasNavigation: config.navigation.brandNavigation || false,
    },
    {
      key: 'subtype' as keyof FilterFacets,
      label: 'Subtype',
      urlParam: 'subtype',
      isVisible: config.visibleFilters.subtype || false,
      hasNavigation: false,
    },
    {
      key: 'classification' as keyof FilterFacets,
      label: 'Classification',
      urlParam: 'classification',
      isVisible: config.visibleFilters.classification || false,
      hasNavigation: false,
    },
    {
      key: 'size' as keyof FilterFacets,
      label: 'Size',
      urlParam: 'size',
      isVisible: config.visibleFilters.size || false,
      hasNavigation: false,
    },
  ].filter((section) => section.isVisible);

  // State for expanded sections - use config default or fallback
  const defaultSections = config.defaultOpenSections || [];
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(defaultSections));

  // State for showing all brands
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [showAllSubtypes, setShowAllSubtypes] = useState(false);
  const [showAllSizes, setShowAllSizes] = useState(false);

  // Refs for the "view more/less" toggles. On some pages toggling one re-renders the options
  // list in a way that drops focus to the body (ejecting a keyboard user from the filter
  // menu). After toggling we re-focus the button so the user stays put and can Tab on into the
  // newly revealed options. rAF runs after React commits the new DOM, so the ref points at the
  // current button even if it was remounted.
  const brandMoreRef = useRef<HTMLButtonElement>(null);
  const subtypeMoreRef = useRef<HTMLButtonElement>(null);
  const sizeMoreRef = useRef<HTMLButtonElement>(null);
  // After a toggle: on EXPAND move focus to the first option in the group so the user can Tab
  // forward through the newly revealed filters (the toggle sits at the END of the list, so
  // focusing it would send forward-Tab into the NEXT section instead). On COLLAPSE keep focus
  // on the button. rAF runs after React commits, so refs/DOM are current even if remounted.
  const focusAfterToggle = (willExpand: boolean, ref: React.RefObject<HTMLButtonElement>) => {
    requestAnimationFrame(() => {
      if (willExpand) {
        const group = ref.current?.closest('[role="group"]');
        const firstOption = group?.querySelector<HTMLInputElement>('input[type="checkbox"]');
        (firstOption || ref.current)?.focus();
      } else {
        ref.current?.focus();
      }
    });
  };

  // Focus trap ref for mobile drawer
  const focusTrapRef = useFocusTrap(trapFocus);

  // Toggle section expansion (allow multiple sections open)
  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionKey)) {
        newSet.delete(sectionKey);
      } else {
        newSet.add(sectionKey);
      }
      return newSet;
    });
  };

  // Parse multi-select filter values (comma-separated)
  const parseFilterValue = (value: string): string[] => {
    return value
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
  };

  // Create filter string for multi-select
  const createMultiSelectFilter = (values: string[]): string => {
    return values.join(',');
  };

  // Handler: Update URL parameters for filters
  const updateSearchParams = (key: string, value: string) => {
    // Mark this as a manual change (prevent auto-clear from running immediately)
    isManualChange.current = true;

    // Trigger external loading state
    onLoadingChange?.(true);

    // Check if this is a navigation filter (only for brands on brand pages)
    const section = FILTER_SECTIONS.find((s) => s.urlParam === key);

    // Only handle brand navigation (categories are now multi-select filters)
    if (section?.hasNavigation && section.key === 'brand') {
      const formattedSlug = toBrandSlug(value);
      klaviyo.track('Brand Selected', {
        brand_name: value,
        previous_brand: config.currentContext?.brandLabel,
        source: `${config.pageType}_filter_menu`,
        timestamp: new Date().toISOString(),
      });
      navigate(`/brand/${formattedSlug}`);
      return;
    }

    // Multi-select behavior for all filters (including categories)
    // Categories now work together with brands and other filters (like Embarc)
    const newParams = new URLSearchParams(searchParams);

    if (!value) {
      newParams.delete(key);
    } else {
      const currentValues = parseFilterValue(newParams.get(key) || '');
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];

      if (newValues.length === 0) {
        newParams.delete(key);
      } else {
        newParams.set(key, createMultiSelectFilter(newValues));
      }
    }

    // Reset to page 1 when filters change
    newParams.delete('page');
    startTransition(() => {
      // Use preventScrollReset to avoid double loading state
      setSearchParams(newParams, { preventScrollReset: true });
    });

    // Track filter selection
    klaviyo.track('Filter Applied', {
      page_type: config.pageType,
      filter_type: section?.label || key,
      filter_value: value,
      action: searchParams.get(key)?.includes(value) ? 'remove' : 'add',
      timestamp: new Date().toISOString(),
    });
  };

  // Handler: Switch category (for category pages)
  const switchCategory = (categorySlug: string) => {
    // Auto-collapse category section after selection (single-select UX)
    setExpandedSections(new Set());

    klaviyo.track('Category Selected', {
      category_name: categorySlug,
      previous_category: config.currentContext?.categorySlug,
      source: 'category_filter_menu',
      timestamp: new Date().toISOString(),
    });
    navigate(`/category/${categorySlug}`);
  };

  // Handler: Switch brand (for brand pages)
  const switchBrand = (brandValue: string) => {
    // Auto-collapse brand section after selection (single-select UX)
    setExpandedSections(new Set());

    const formattedSlug = toBrandSlug(brandValue);
    klaviyo.track('Brand Selected', {
      brand_name: brandValue,
      previous_brand: config.currentContext?.brandLabel,
      source: 'brand_filter_menu',
      timestamp: new Date().toISOString(),
    });
    navigate(`/brand/${formattedSlug}`);
  };

  // Handler: Clear all filters
  const clearAllFilters = () => {
    isManualChange.current = true;
    onLoadingChange?.(true);

    const newParams = new URLSearchParams();
    // Preserve non-filter params (sort, search, etc.)
    const preserveParams = ['sort', 'search'];
    preserveParams.forEach((param) => {
      const value = searchParams.get(param);
      if (value) {
        newParams.set(param, value);
      }
    });

    startTransition(() => {
      setSearchParams(newParams, { replace: true });
    });

    klaviyo.track('Filters Cleared', {
      page_type: config.pageType,
      timestamp: new Date().toISOString(),
    });
  };

  // Get current filter selections - includes deals filter
  const getCurrentSelections = (): Record<string, string[]> => {
    const selections: Record<string, string[]> = {};
    FILTER_SECTIONS.forEach((section) => {
      const value = searchParams.get(section.urlParam) || '';
      if (value) {
        selections[section.key] = parseFilterValue(value);
      }
    });
    // Include deals filter if active
    if (searchParams.get('deals') === 'true') {
      selections.discount_applied = ['true'];
    }
    return selections;
  };

  // 🚀 SAFE CLIENT-SIDE ENHANCEMENT: Show grayed out options after hydration
  // This approach never affects SSR - only enhances the client experience
  const [isClientReady, setIsClientReady] = useState(false);

  useEffect(() => {
    // Only enable enhanced display after client hydration is complete
    setIsClientReady(true);
  }, []);

  // Create filter options - Hide options with 0 count (unless selected)
  // Matches Embarc's behavior: only show available options, hide unavailable ones
  const createFilterOptions = (
    sectionKey: keyof FilterFacets,
    urlParam: string,
  ): FilterOption[] => {
    // Enhanced null/undefined safety (matches Embarc pattern)
    const contextualOptions = contextualFacets?.[sectionKey] || [];
    const baseOptions = baseFacets?.[sectionKey] || [];
    const currentSelections = parseFilterValue(searchParams.get(urlParam) || '');

    const allOptionsMap = new Map<string, { value: string; count: number; isAvailable: boolean }>();

    // Add contextual options (available with current filters)
    contextualOptions.forEach((option: any) => {
      allOptionsMap.set(option.value, {
        value: option.value,
        count: option.count,
        isAvailable: option.count > 0,
      });
    });

    // Add currently selected options even if they have 0 count (so user can unselect them)
    currentSelections.forEach((selectedValue) => {
      if (!allOptionsMap.has(selectedValue)) {
        const contextualOption = contextualOptions.find((ctx: any) => ctx.value === selectedValue);
        allOptionsMap.set(selectedValue, {
          value: selectedValue,
          count: contextualOption ? contextualOption.count : 0,
          isAvailable: contextualOption ? contextualOption.count > 0 : false,
        });
      }
    });

    // Convert to FilterOption array and filter out options with 0 count (unless selected)
    return Array.from(allOptionsMap.values())
      .filter((option) => {
        // Always show selected options (even with 0 count) so user can unselect them
        if (currentSelections.includes(option.value)) {
          return true;
        }
        // Hide non-selected options with 0 count (matches Embarc behavior)
        return option.count > 0;
      })
      .map((option) => ({
        value: option.value,
        count: option.count,
        isSelected: currentSelections.includes(option.value),
        isAvailable: option.isAvailable,
      }))
      .sort((a, b) => {
        // Sort by count in descending order (highest count first)
        // If counts are equal, sort alphabetically as fallback
        if (b.count !== a.count) {
          return b.count - a.count;
        }
        return a.value.localeCompare(b.value);
      });
  };

  // Handle keyboard navigation for accessibility - SAME AS ORIGINAL
  useEffect(() => {
    if (!trapFocus) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onRequestClose) {
        onRequestClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [trapFocus, onRequestClose]);

  // Count active filters - SAME AS ORIGINAL
  const activeFilterCount = Object.values(getCurrentSelections()).reduce(
    (count, selections) => count + selections.length,
    0,
  );

  return (
    <div
      ref={trapFocus ? focusTrapRef : undefined}
      className={`font-helvetica-neue relative flex w-full min-w-0 flex-col items-start bg-white ${trapFocus ? 'flex h-full flex-col p-3' : 'p-3 lg:px-0 lg:pt-0'}`}
      aria-label="Product filters"
      role={trapFocus ? 'dialog' : 'region'}
      {...(trapFocus ? { 'aria-modal': 'true' as const, tabIndex: -1 } : {})}
    >
      {/* Loading Overlay - Shows when filters are being applied or products are loading */}
      {/* Matches Embarc's implementation exactly */}
      {isFilterLoading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/50">
          <div className="rounded-full bg-white p-3 shadow-lg">
            <div
              className="h-6 w-6 animate-spin rounded-full border-3 border-[#264B9A] border-t-transparent"
              style={{ animationDuration: '0.6s' }}
            />
          </div>
        </div>
      )}
      {/* Mobile Header - Show when trapFocus is true (mobile drawer) */}
      {trapFocus && (
        <div className="flex w-full shrink-0 flex-col gap-2 border-b border-gray-200 p-1">
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-2">
              <h2
                id={`mobile-filter-title-${uniqueId}`}
                className="text-lg font-semibold text-gray-900"
              >
                Product Filters
              </h2>
              <span className="text-sm text-[#4A5565]" aria-live="polite">
                ({total.toLocaleString()} results)
              </span>
            </div>
            {onRequestClose && (
              <button
                onClick={onRequestClose}
                className="relative inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full p-2 focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] focus-visible:outline-none"
                aria-label="Close filters"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Selected Filters Tags - Mobile */}
          {activeFilterCount > 0 && (
            <div
              className="scrollbar-hide flex w-full items-center gap-2 overflow-x-auto"
              role="group"
              aria-label="Selected filters"
            >
              <div className="flex items-center gap-2 overflow-x-auto">
                {getCurrentSelections() &&
                  Object.entries(getCurrentSelections()).map(([key, values]) =>
                    values.map((value) => {
                      // Handle deals filter specially
                      if (key === 'discount_applied' && value === 'true') {
                        return (
                          <button
                            key={`${key}-${value}`}
                            className="flex flex-shrink-0 items-center gap-1 rounded-lg border border-[#D7DFEF] bg-white px-4 py-1 text-sm whitespace-nowrap text-[#94A3B8] hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] focus-visible:outline-none"
                            onClick={() => {
                              const newParams = new URLSearchParams(searchParams);
                              newParams.delete('deals');
                              newParams.delete('page');
                              startTransition(() => {
                                setSearchParams(newParams, { preventScrollReset: true });
                              });
                            }}
                            aria-label="Remove deals filter"
                          >
                            Deals
                            <svg
                              className="h-3 w-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        );
                      }
                      const section = FILTER_SECTIONS.find((s) => s.key === key);
                      return (
                        <button
                          key={`${key}-${value}`}
                          className="flex flex-shrink-0 items-center gap-1 rounded-lg border border-[#D7DFEF] bg-white px-4 py-1 text-sm whitespace-nowrap text-[#94A3B8] hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] focus-visible:outline-none"
                          onClick={() => {
                            if (section) {
                              updateSearchParams(section.urlParam, value);
                            }
                          }}
                          aria-label={`Remove ${key === 'size' ? formatProductSize(value) : value} filter`}
                        >
                          {key === 'size' ? formatProductSize(value) : toProperCase(value)}
                          <svg
                            className="h-3 w-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      );
                    }),
                  )}
              </div>
              <button
                className="ml-2 flex-shrink-0 text-sm whitespace-nowrap text-[#DC2626] hover:underline focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] focus-visible:outline-none"
                onClick={clearAllFilters}
                aria-label="Clear all filters"
              >
                Clear All
              </button>
            </div>
          )}
        </div>
      )}

      {/* Desktop Header - Show when trapFocus is false (desktop sidebar) */}
      {!trapFocus && (
        <header
          className="flex w-full flex-col items-start gap-2 p-1 lg:pt-0"
          aria-label="Product filters"
        >
          <div className="flex w-full flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            {/* Not a heading: keeping it out of the heading order avoids an h2 appearing before
                the page's h1 (the surrounding <header> still labels the region). */}
            <div className="font-helvetica-neue text-xl leading-7 font-bold tracking-[0.02em] text-[#181818]">
              Product Filters
            </div>
            <div className="shrink-0">
              <span className="text-sm whitespace-nowrap text-[#4A5565]" aria-live="polite">
                ({total.toLocaleString()} results)
              </span>
            </div>
          </div>

          {/* Deals Toggle - Desktop - Above filter tags */}
          {config.visibleFilters.discount_applied &&
            (calculatedDealsAvailable > 0 || searchParams.get('deals') === 'true') && (
              <div className="w-full border-b border-gray-200 pb-2">
                <div className="flex items-center justify-between px-1 py-2">
                  <span className="text-base font-medium tracking-[0.04em] text-black">Deals</span>
                  <button
                    type="button"
                    onClick={() => {
                      isManualChange.current = true;
                      onLoadingChange?.(true);

                      const isDealsActive = searchParams.get('deals') === 'true';
                      const newParams = new URLSearchParams(searchParams);
                      if (isDealsActive) {
                        newParams.delete('deals');
                      } else {
                        newParams.set('deals', 'true');
                        // Clear search parameter when applying deals filter
                        newParams.delete('search');
                      }
                      newParams.delete('page'); // Reset to page 1

                      startTransition(() => {
                        setSearchParams(newParams, { preventScrollReset: true });
                      });

                      klaviyo.track('Filter Applied', {
                        page_type: config.pageType,
                        filter_type: 'deals',
                        filter_value: isDealsActive ? 'off' : 'on',
                        action: isDealsActive ? 'remove' : 'add',
                        timestamp: new Date().toISOString(),
                      });
                    }}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:ring-2 focus-visible:ring-[#264B9A] focus-visible:ring-offset-2 focus-visible:outline-none ${
                      searchParams.get('deals') === 'true'
                        ? 'bg-[#264B9A]'
                        : calculatedDealsAvailable > 0
                          ? 'bg-gray-200'
                          : 'cursor-not-allowed bg-gray-200 opacity-50'
                    }`}
                    disabled={
                      calculatedDealsAvailable === 0 && searchParams.get('deals') !== 'true'
                    }
                    aria-label={`${searchParams.get('deals') === 'true' ? 'Disable' : 'Enable'} deals filter`}
                    aria-describedby="deals-toggle-description-desktop"
                    role="switch"
                    aria-checked={searchParams.get('deals') === 'true'}
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white ring-0 shadow transition duration-200 ease-in-out ${
                        searchParams.get('deals') === 'true' ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  {/* Hidden description for screen readers */}
                  <span id="deals-toggle-description-desktop" className="sr-only">
                    Toggle to show only products with deals and discounts
                  </span>
                </div>
              </div>
            )}

          {/* Selected Filters Tags - EXACT SAME AS ORIGINAL */}
          {activeFilterCount > 0 && (
            <div
              className="scrollbar-hide flex w-full items-center gap-2 overflow-x-auto"
              role="group"
              aria-label="Selected filters"
            >
              <div className="flex items-center gap-2 overflow-x-auto">
                {getCurrentSelections() &&
                  Object.entries(getCurrentSelections()).map(([key, values]) =>
                    values.map((value) => {
                      // Handle deals filter specially
                      if (key === 'discount_applied' && value === 'true') {
                        return (
                          <button
                            key={`${key}-${value}`}
                            className="flex flex-shrink-0 items-center gap-1 rounded-lg border border-[#D7DFEF] bg-white px-4 py-1 text-sm whitespace-nowrap text-[#94A3B8] hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] focus-visible:outline-none"
                            onClick={() => {
                              const newParams = new URLSearchParams(searchParams);
                              newParams.delete('deals');
                              newParams.delete('page');
                              startTransition(() => {
                                setSearchParams(newParams, { preventScrollReset: true });
                              });
                            }}
                            aria-label="Remove deals filter"
                          >
                            Deals
                            <svg
                              className="h-3 w-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        );
                      }
                      const section = FILTER_SECTIONS.find((s) => s.key === key);
                      return (
                        <button
                          key={`${key}-${value}`}
                          className="flex flex-shrink-0 items-center gap-1 rounded-lg border border-[#D7DFEF] bg-white px-4 py-1 text-sm whitespace-nowrap text-[#94A3B8] hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] focus-visible:outline-none"
                          onClick={() => {
                            if (section) {
                              updateSearchParams(section.urlParam, value);
                            }
                          }}
                          aria-label={`Remove ${key === 'size' ? formatProductSize(value) : value} filter`}
                        >
                          {key === 'size' ? formatProductSize(value) : toProperCase(value)}
                          <svg
                            className="h-3 w-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      );
                    }),
                  )}
              </div>
              <button
                className="ml-2 flex-shrink-0 text-sm whitespace-nowrap text-[#DC2626] hover:underline focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] focus-visible:outline-none"
                onClick={clearAllFilters}
                aria-label="Clear all filters"
              >
                Clear All
              </button>
            </div>
          )}
        </header>
      )}

      {/* Brand Switch Section (for brand pages) - NEW BUT SAME STRUCTURE */}
      {/* Enhanced null/undefined safety (matches Embarc pattern) */}
      {config.pageType === 'brand' && config.currentContext?.brandSlug && baseFacets?.brand && (
        <div className="w-full border-b border-gray-200">
          <button
            className="flex min-h-[44px] w-full items-center justify-between rounded px-1 py-2 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] focus-visible:outline-none"
            onClick={() => toggleSection('brand')}
            aria-expanded={expandedSections.has('brand')}
            aria-controls={`brand-switch-${uniqueId}`}
          >
            <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5 pr-3 text-left">
              <span className="text-base font-medium tracking-[0.04em] text-black">
                Switch Brand
              </span>
              {config.currentContext?.brandLabel && (
                <span className="text-sm font-medium text-[#264B9A]">
                  {config.currentContext.brandLabel}
                </span>
              )}
            </div>
            <span aria-hidden="true" className="text-lg font-normal text-[#0F172A]">
              {expandedSections.has('brand') ? '−' : '+'}
            </span>
          </button>

          {expandedSections.has('brand') && (
            <div
              id={`brand-switch-${uniqueId}`}
              className="flex flex-col gap-0.5 px-0 pt-1 pb-2"
              role="group"
              aria-label="Brand options"
            >
              {(() => {
                // Priority brands that should always be included in top 10
                const priorityBrands = ['CATALYST', 'CAT4', 'DELTA WHIP', 'MJUICE', 'WHT ASH'];

                // Sort all brands by count (descending) for popularity
                // Enhanced null/undefined safety (matches Embarc pattern)
                const sortedBrands = [...(baseFacets?.brand || [])].sort(
                  (a, b) => b.count - a.count,
                );

                // Find priority brands that are actually available (maintain their count order)
                const priorityPresent = sortedBrands.filter((b) =>
                  priorityBrands.includes(b.value.toUpperCase()),
                );

                // Get other brands (excluding priority brands)
                const otherBrands = sortedBrands.filter(
                  (b) => !priorityBrands.includes(b.value.toUpperCase()),
                );

                // Take top brands by popularity
                // Include all priority brands + fill remaining slots with popular brands (10 total)
                const remainingSlots = Math.max(0, 10 - priorityPresent.length);
                const topOtherBrands = otherBrands.slice(0, remainingSlots);

                // Combine priority brands + top popular brands, keep popularity order
                // Priority brands first (sorted by count), then top non-priority brands
                const topBrands = [...priorityPresent, ...topOtherBrands];

                // Return brands to display (top 10 or all if showAllBrands is true)
                return showAllBrands
                  ? [...(baseFacets?.brand || [])].sort((a, b) =>
                      a.value.localeCompare(b.value, undefined, { sensitivity: 'base' }),
                    ) // All brands sorted alphabetically when "View More" is clicked
                  : topBrands; // Top 10 with priority brands always included
              })().map((brand) => (
                <button
                  key={brand.value}
                  className={`flex min-h-[44px] w-full cursor-pointer items-center gap-2.5 rounded-md px-1 py-1 text-left text-sm hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] focus-visible:outline-none lg:min-h-9 ${
                    brand.value === config.currentContext?.brandLabel
                      ? 'bg-blue-100 font-medium text-blue-900'
                      : ''
                  }`}
                  onClick={() => switchBrand(brand.value)}
                  disabled={brand.value === config.currentContext?.brandLabel}
                  aria-label={`Switch to ${brand.value} brand`}
                >
                  <span className="min-w-0 flex-1 leading-5 break-words text-[#181818]">
                    {brand.value}
                    {brand.value === config.currentContext?.brandLabel && ' (current)'}
                  </span>
                  <span className="ml-auto shrink-0 pr-1 text-xs whitespace-nowrap text-[#64748B] tabular-nums">
                    ({brand.count})
                  </span>
                </button>
              ))}

              {/* View More/Less Button */}
              {/* Enhanced null/undefined safety (matches Embarc pattern) */}
              {(baseFacets?.brand?.length || 0) > 10 && (
                <button
                  className="mt-2 flex min-h-[44px] cursor-pointer items-center justify-center rounded border border-gray-200 px-2 py-1 text-sm text-blue-600 hover:bg-blue-50 focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] focus-visible:outline-none"
                  ref={brandMoreRef}
                  onClick={() => {
                    const willExpand = !showAllBrands;
                    setShowAllBrands(willExpand);
                    focusAfterToggle(willExpand, brandMoreRef);
                  }}
                  aria-label={showAllBrands ? 'Show fewer brands' : 'Show more brands'}
                  aria-expanded={showAllBrands}
                >
                  {showAllBrands
                    ? 'View Less'
                    : `View More (${(baseFacets?.brand?.length || 0) - 10} more)`}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Deals Toggle - Mobile - Above filter tags in header */}
      {trapFocus &&
        config.visibleFilters.discount_applied &&
        (calculatedDealsAvailable > 0 || searchParams.get('deals') === 'true') && (
          <div className="w-full shrink-0 border-b border-gray-200 pb-2">
            <div className="flex items-center justify-between px-1 py-2">
              <span className="text-base font-medium tracking-[0.04em] text-black">Deals</span>
              <button
                type="button"
                onClick={() => {
                  isManualChange.current = true;
                  onLoadingChange?.(true);

                  const isDealsActive = searchParams.get('deals') === 'true';
                  const newParams = new URLSearchParams(searchParams);
                  if (isDealsActive) {
                    newParams.delete('deals');
                  } else {
                    newParams.set('deals', 'true');
                    // Clear search parameter when applying deals filter
                    newParams.delete('search');
                  }
                  newParams.delete('page'); // Reset to page 1

                  startTransition(() => {
                    setSearchParams(newParams, { preventScrollReset: true });
                  });

                  klaviyo.track('Filter Applied', {
                    page_type: config.pageType,
                    filter_type: 'deals',
                    filter_value: isDealsActive ? 'off' : 'on',
                    action: isDealsActive ? 'remove' : 'add',
                    timestamp: new Date().toISOString(),
                  });
                }}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:ring-2 focus-visible:ring-[#264B9A] focus-visible:ring-offset-2 focus-visible:outline-none ${
                  searchParams.get('deals') === 'true'
                    ? 'bg-[#264B9A]'
                    : calculatedDealsAvailable > 0
                      ? 'bg-gray-200'
                      : 'cursor-not-allowed bg-gray-200 opacity-50'
                }`}
                disabled={calculatedDealsAvailable === 0 && searchParams.get('deals') !== 'true'}
                aria-label={`${searchParams.get('deals') === 'true' ? 'Disable' : 'Enable'} deals filter`}
                aria-describedby="deals-toggle-description-mobile"
                role="switch"
                aria-checked={searchParams.get('deals') === 'true'}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white ring-0 shadow transition duration-200 ease-in-out ${
                    searchParams.get('deals') === 'true' ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              {/* Hidden description for screen readers */}
              <span id="deals-toggle-description-mobile" className="sr-only">
                Toggle to show only products with deals and discounts
              </span>
            </div>
          </div>
        )}

      {/* Filter Sections - Disabled during loading */}
      {/* Make scrollable on mobile with padding for fixed button - scrollbar hidden */}
      <div
        className={`min-h-0 w-full flex-1 overflow-y-auto ${
          isFilterLoading ? 'pointer-events-none' : ''
        } ${trapFocus ? 'scrollbar-hide pb-20' : ''}`}
      >
        {FILTER_SECTIONS.map((section, index, filteredSections) => {
          const options = createFilterOptions(section.key, section.urlParam);
          const isExpanded = expandedSections.has(section.key);
          const hasActiveFilters = options.some((option) => option.isSelected);

          if (options.length === 0) return null;

          const isLastSection = index === filteredSections.length - 1;

          // Regular dropdown sections for filters
          return (
            <div
              key={section.key}
              className={`w-full ${!isLastSection ? 'border-b border-gray-200' : ''}`}
            >
              <button
                className="flex min-h-[44px] w-full items-center justify-between rounded px-1 py-2 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] focus-visible:outline-none"
                onClick={() => toggleSection(section.key)}
                aria-expanded={isExpanded}
                aria-controls={`${section.key}-filters-${uniqueId}`}
              >
                <span className="text-base font-medium tracking-[0.04em] text-black">
                  {section.label}
                </span>
                <span aria-hidden="true" className="text-lg font-normal text-[#0F172A]">
                  {isExpanded ? '−' : '+'}
                </span>
              </button>

              {isExpanded && (
                <div
                  id={`${section.key}-filters-${uniqueId}`}
                  className="flex flex-col gap-0.5 px-0 pt-1 pb-2"
                  role="group"
                  aria-label={`${section.label} filters`}
                >
                  {(() => {
                    // For brand filter, apply priority + popularity ordering
                    if (section.key === 'brand') {
                      // Priority brands that should always be included in top 10
                      const priorityBrands = [
                        'CATALYST',
                        'CAT4',
                        'DELTA WHIP',
                        'MJUICE',
                        'WHT ASH',
                      ];

                      // Options are already sorted by count (descending) from createFilterOptions
                      // Find priority brands that are actually available (maintain their count order)
                      const priorityPresent = options.filter((opt) =>
                        priorityBrands.includes(opt.value.toUpperCase()),
                      );

                      // Get other brands (excluding priority brands)
                      const otherBrands = options.filter(
                        (opt) => !priorityBrands.includes(opt.value.toUpperCase()),
                      );

                      // Take top brands by popularity
                      // Include all priority brands + fill remaining slots with popular brands (10 total)
                      const remainingSlots = Math.max(0, 10 - priorityPresent.length);
                      const topOtherBrands = otherBrands.slice(0, remainingSlots);

                      // Combine priority brands + top popular brands, keep popularity order
                      // Priority brands first (sorted by count), then top non-priority brands
                      const topBrands = [...priorityPresent, ...topOtherBrands];

                      // Return brands to display (top 10 or all if showAllBrands is true)
                      return showAllBrands
                        ? [...options].sort((a, b) =>
                            a.value.localeCompare(b.value, undefined, { sensitivity: 'base' }),
                          ) // All brands sorted alphabetically when "View More" is clicked
                        : topBrands; // Top 10 with priority brands always included
                    }

                    // For subtype filter, limit to top 10 by popularity
                    if (section.key === 'subtype') {
                      // Options are already sorted by count (descending) from createFilterOptions
                      // Return top 10 or all if showAllSubtypes is true
                      return showAllSubtypes
                        ? options // All subtypes already sorted by popularity
                        : options.slice(0, 10); // Top 10 subtypes
                    }

                    // For size filter, limit to top 10 by popularity
                    if (section.key === 'size') {
                      // Options are already sorted by count (descending) from createFilterOptions
                      // Return top 10 or all if showAllSizes is true
                      return showAllSizes
                        ? options // All sizes already sorted by popularity
                        : options.slice(0, 10); // Top 10 sizes
                    }

                    // For non-brand, non-subtype, non-size filters, return options as-is (already sorted by count)
                    return options;
                  })().map((option) => {
                    const inputId = `${section.urlParam}-${option.value.replace(/\s+/g, '-').toLowerCase()}-${uniqueId}-input`;

                    // Options are already filtered to only show available ones (count > 0) or selected ones
                    return (
                      <label
                        key={option.value}
                        className={`flex min-h-[44px] cursor-pointer items-center gap-2.5 rounded-md px-1 py-1 text-sm transition-colors lg:min-h-9 ${option.isSelected ? 'bg-[#264B9A]/[0.06]' : 'hover:bg-[#264B9A]/[0.04]'}`}
                      >
                        <input
                          id={inputId}
                          type="checkbox"
                          checked={option.isSelected}
                          onChange={() => updateSearchParams(section.urlParam, option.value)}
                          disabled={isFilterLoading}
                          className="catalyst-facet-checkbox"
                          aria-label={`${option.isSelected ? 'Remove' : 'Add'} ${section.key === 'size' ? formatProductSize(option.value) : option.value} filter`}
                        />
                        <span className="min-w-0 flex-1 leading-5 break-words text-[#181818]">
                          {section.key === 'size'
                            ? formatProductSize(option.value)
                            : toProperCase(option.value)}
                        </span>
                        <span className="ml-auto shrink-0 pr-1 text-xs whitespace-nowrap text-[#64748B] tabular-nums">
                          ({option.count})
                        </span>
                      </label>
                    );
                  })}

                  {/* View More/Less Button for Brands */}
                  {section.key === 'brand' && options.length > 10 && (
                    <button
                      className="mt-2 flex min-h-[44px] cursor-pointer items-center justify-center rounded border border-gray-200 px-2 py-1 text-sm text-blue-600 hover:bg-blue-50 focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] focus-visible:outline-none"
                      ref={brandMoreRef}
                      onClick={() => {
                        const willExpand = !showAllBrands;
                        setShowAllBrands(willExpand);
                        focusAfterToggle(willExpand, brandMoreRef);
                      }}
                      aria-label={showAllBrands ? 'Show fewer brands' : 'Show more brands'}
                      aria-expanded={showAllBrands}
                    >
                      {showAllBrands ? 'View Less' : `View More (${options.length - 10} more)`}
                    </button>
                  )}

                  {/* View More/Less Button for Subtypes */}
                  {section.key === 'subtype' && options.length > 10 && (
                    <button
                      className="mt-2 flex min-h-[44px] cursor-pointer items-center justify-center rounded border border-gray-200 px-2 py-1 text-sm text-blue-600 hover:bg-blue-50 focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] focus-visible:outline-none"
                      ref={subtypeMoreRef}
                      onClick={() => {
                        const willExpand = !showAllSubtypes;
                        setShowAllSubtypes(willExpand);
                        focusAfterToggle(willExpand, subtypeMoreRef);
                      }}
                      aria-label={showAllSubtypes ? 'Show fewer subtypes' : 'Show more subtypes'}
                      aria-expanded={showAllSubtypes}
                    >
                      {showAllSubtypes ? 'View Less' : `View More (${options.length - 10} more)`}
                    </button>
                  )}

                  {/* View More/Less Button for Sizes */}
                  {section.key === 'size' && options.length > 10 && (
                    <button
                      className="mt-2 flex min-h-[44px] cursor-pointer items-center justify-center rounded border border-gray-200 px-2 py-1 text-sm text-blue-600 hover:bg-blue-50 focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] focus-visible:outline-none"
                      ref={sizeMoreRef}
                      onClick={() => {
                        const willExpand = !showAllSizes;
                        setShowAllSizes(willExpand);
                        focusAfterToggle(willExpand, sizeMoreRef);
                      }}
                      aria-label={showAllSizes ? 'Show fewer sizes' : 'Show more sizes'}
                      aria-expanded={showAllSizes}
                    >
                      {showAllSizes ? 'View Less' : `View More (${options.length - 10} more)`}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer actions: Show results - Mobile only, fixed to bottom, full width */}
      {trapFocus && (
        <div className="fixed right-0 bottom-0 left-0 z-50 flex w-full items-center justify-center border-t border-gray-200 bg-white p-4 shadow-[0_-2px_8px_1px_rgba(0,0,0,0.08)]">
          <button
            className="flex min-h-[44px] w-full items-center justify-center rounded bg-[#264B9A] px-4 py-2 text-base font-medium text-white focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:outline-none"
            onClick={() => {
              if (onRequestClose) onRequestClose();
            }}
            aria-label={`Apply filters and show results (${total.toLocaleString()} products)`}
          >
            Show Results ({total.toLocaleString()})
          </button>
        </div>
      )}
    </div>
  );
}
